/* eslint-disable indent */
/* eslint-disable no-nested-ternary */

import { ComponentProps, ReactNode, Ref } from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';

import { Link } from './link';

const styles = {
  base: [
    // Base
    'relative isolate inline-flex items-baseline justify-center gap-x-2 rounded-lg border text-base/6 font-semibold',
    // Sizing
    'px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6',
    // Focus
    'focus:outline-hidden data-hover:outline data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary-500',
    // Disabled
    'data-disabled:opacity-50',
    // Icon
    '*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]',
  ],
  solid: [
    // Optical border, implemented as the button background to avoid corner artifacts
    'border-transparent bg-(--btn-border)',
    // Dark mode: border is rendered on `after` so background is set to button background
    'dark:bg-(--btn-bg)',
    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
    'before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-lg)-1px)] before:bg-(--btn-bg)',
    // Drop shadow, applied to the inset `before` layer so it blends with the border
    'before:shadow-sm',
    // Background color is moved to control and shadow is removed in dark mode
    // so hide `before` pseudo
    'dark:before:hidden',
    // Dark mode: Subtle white outline is applied using a border
    'dark:border-white/5',
    // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
    'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-lg)-1px)]',
    // Inner highlight shadow
    'after:shadow-[shadow:inset_0_1px_--theme(--color-white/15%)]',
    // White overlay on hover
    'data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay)',
    // Dark mode: `after` layer expands to cover entire button
    'dark:after:-inset-px dark:after:rounded-lg',
    // Disabled
    'data-disabled:before:shadow-none data-disabled:after:shadow-none',
  ],
  outline: [
    // Base
    'border-gray-950/10 text-gray-950 data-active:bg-gray-950/[2.5%] data-hover:bg-gray-950/[2.5%]',
    // Dark mode
    'dark:border-white/15 dark:text-white dark:[--btn-bg:transparent] dark:data-active:bg-white/5 dark:data-hover:bg-white/5',
    // Icon
    '[--btn-icon:var(--color-gray-500)] data-active:[--btn-icon:var(--color-gray-700)] data-hover:[--btn-icon:var(--color-gray-700)] dark:data-active:[--btn-icon:var(--color-gray-400)] dark:data-hover:[--btn-icon:var(--color-gray-400)]',
  ],
  plain: [
    // Base
    'border-transparent text-gray-950 data-active:bg-gray-950/5 data-hover:bg-gray-950/5',
    // Dark mode
    'dark:text-white dark:data-active:bg-white/10 dark:data-hover:bg-white/10',
    // Icon
    '[--btn-icon:var(--color-gray-500)] data-active:[--btn-icon:var(--color-gray-700)] data-hover:[--btn-icon:var(--color-gray-700)] dark:[--btn-icon:var(--color-gray-500)] dark:data-active:[--btn-icon:var(--color-gray-400)] dark:data-hover:[--btn-icon:var(--color-gray-400)]',
  ],
  colors: {
    'dark/zinc': [
      'text-white [--btn-bg:var(--color-gray-900)] [--btn-border:var(--color-gray-950)]/90 [--btn-hover-overlay:var(--color-white)]/10',
      'dark:text-white dark:[--btn-bg:var(--color-gray-600)] dark:[--btn-hover-overlay:var(--color-white)]/5',
      '[--btn-icon:var(--color-gray-400)] data-active:[--btn-icon:var(--color-gray-300)] data-hover:[--btn-icon:var(--color-gray-300)]',
    ],
    light: [
      'text-gray-950 [--btn-bg:white] [--btn-border:var(--color-gray-950)]/10 [--btn-hover-overlay:var(--color-gray-950)]/[2.5%] data-active:[--btn-border:var(--color-gray-950)]/15 data-hover:[--btn-border:var(--color-gray-950)]/15',
      'dark:text-white dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-gray-800)]',
      '[--btn-icon:var(--color-gray-500)] data-active:[--btn-icon:var(--color-gray-700)] data-hover:[--btn-icon:var(--color-gray-700)] dark:[--btn-icon:var(--color-gray-500)] dark:data-active:[--btn-icon:var(--color-gray-400)] dark:data-hover:[--btn-icon:var(--color-gray-400)]',
    ],
    'dark/white': [
      'text-white [--btn-bg:var(--color-gray-900)] [--btn-border:var(--color-gray-950)]/90 [--btn-hover-overlay:var(--color-white)]/10',
      'dark:text-gray-950 dark:[--btn-bg:white] dark:[--btn-hover-overlay:var(--color-gray-950)]/5',
      '[--btn-icon:var(--color-gray-400)] data-active:[--btn-icon:var(--color-gray-300)] data-hover:[--btn-icon:var(--color-gray-300)] dark:[--btn-icon:var(--color-gray-500)] dark:data-active:[--btn-icon:var(--color-gray-400)] dark:data-hover:[--btn-icon:var(--color-gray-400)]',
    ],
    dark: [
      'text-white [--btn-bg:var(--color-gray-900)] [--btn-border:var(--color-gray-950)]/90 [--btn-hover-overlay:var(--color-white)]/10',
      'dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-gray-800)]',
      '[--btn-icon:var(--color-gray-400)] data-active:[--btn-icon:var(--color-gray-300)] data-hover:[--btn-icon:var(--color-gray-300)]',
    ],
    white: [
      'text-gray-950 [--btn-bg:white] [--btn-border:var(--color-gray-950)]/10 [--btn-hover-overlay:var(--color-gray-950)]/[2.5%] data-active:[--btn-border:var(--color-gray-950)]/15 data-hover:[--btn-border:var(--color-gray-950)]/15',
      'dark:[--btn-hover-overlay:var(--color-gray-950)]/5',
      '[--btn-icon:var(--color-gray-400)] data-active:[--btn-icon:var(--color-gray-500)] data-hover:[--btn-icon:var(--color-gray-500)]',
    ],
    zinc: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-gray-600)] [--btn-border:var(--color-gray-700)]/90',
      'dark:[--btn-hover-overlay:var(--color-white)]/5',
      '[--btn-icon:var(--color-gray-400)] data-active:[--btn-icon:var(--color-gray-300)] data-hover:[--btn-icon:var(--color-gray-300)]',
    ],
    indigo: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-indigo-500)] [--btn-border:var(--color-indigo-600)]/90',
      '[--btn-icon:var(--color-indigo-300)] data-active:[--btn-icon:var(--color-indigo-200)] data-hover:[--btn-icon:var(--color-indigo-200)]',
    ],
    cyan: [
      'text-cyan-950 [--btn-bg:var(--color-cyan-300)] [--btn-border:var(--color-cyan-400)]/80 [--btn-hover-overlay:var(--color-white)]/25',
      '[--btn-icon:var(--color-cyan-500)]',
    ],
    red: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-red-600)] [--btn-border:var(--color-red-700)]/90',
      '[--btn-icon:var(--color-red-300)] data-active:[--btn-icon:var(--color-red-200)] data-hover:[--btn-icon:var(--color-red-200)]',
    ],
    orange: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-orange-500)] [--btn-border:var(--color-orange-600)]/90',
      '[--btn-icon:var(--color-orange-300)] data-active:[--btn-icon:var(--color-orange-200)] data-hover:[--btn-icon:var(--color-orange-200)]',
    ],
    amber: [
      'text-amber-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-amber-400)] [--btn-border:var(--color-amber-500)]/80',
      '[--btn-icon:var(--color-amber-600)]',
    ],
    yellow: [
      'text-yellow-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-yellow-300)] [--btn-border:var(--color-yellow-400)]/80',
      '[--btn-icon:var(--color-yellow-600)] data-active:[--btn-icon:var(--color-yellow-700)] data-hover:[--btn-icon:var(--color-yellow-700)]',
    ],
    lime: [
      'text-lime-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-lime-300)] [--btn-border:var(--color-lime-400)]/80',
      '[--btn-icon:var(--color-lime-600)] data-active:[--btn-icon:var(--color-lime-700)] data-hover:[--btn-icon:var(--color-lime-700)]',
    ],
    green: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-green-600)] [--btn-border:var(--color-green-700)]/90',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    emerald: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-emerald-600)] [--btn-border:var(--color-emerald-700)]/90',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    teal: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-teal-600)] [--btn-border:var(--color-teal-700)]/90',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    sky: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-sky-500)] [--btn-border:var(--color-sky-600)]/80',
      '[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80',
    ],
    primary: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-primary-600)] [--btn-border:var(--color-primary-700)]/90',
      '[--btn-icon:var(--color-primary-400)] data-active:[--btn-icon:var(--color-primary-300)] data-hover:[--btn-icon:var(--color-primary-300)]',
    ],
    violet: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-primary-500)] [--btn-border:var(--color-primary-600)]/90',
      '[--btn-icon:var(--color-primary-300)] data-active:[--btn-icon:var(--color-primary-200)] data-hover:[--btn-icon:var(--color-primary-200)]',
    ],
    purple: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-primary-500)] [--btn-border:var(--color-primary-600)]/90',
      '[--btn-icon:var(--color-primary-300)] data-active:[--btn-icon:var(--color-primary-200)] data-hover:[--btn-icon:var(--color-primary-200)]',
    ],
    fuchsia: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-fuchsia-500)] [--btn-border:var(--color-fuchsia-600)]/90',
      '[--btn-icon:var(--color-fuchsia-300)] data-active:[--btn-icon:var(--color-fuchsia-200)] data-hover:[--btn-icon:var(--color-fuchsia-200)]',
    ],
    pink: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-pink-500)] [--btn-border:var(--color-pink-600)]/90',
      '[--btn-icon:var(--color-pink-300)] data-active:[--btn-icon:var(--color-pink-200)] data-hover:[--btn-icon:var(--color-pink-200)]',
    ],
    rose: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-rose-500)] [--btn-border:var(--color-rose-600)]/90',
      '[--btn-icon:var(--color-rose-300)] data-active:[--btn-icon:var(--color-rose-200)] data-hover:[--btn-icon:var(--color-rose-200)]',
    ],
  },
};

interface ITouchTargetProps {
  children: ReactNode;
}

/**
 * -----------------------------------------------------------------------------
 * Provides an option to expand the touch target on mobile devices.
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: ITouchTargetProps) {
  return (
    <>
      <span
        aria-hidden="true"
        className="
          size-[max(100%,2.75rem)] absolute left-1/2 top-1/2 -translate-x-1/2
          -translate-y-1/2 [@media(pointer:fine)]:hidden
        "
      />
      {children}
    </>
  );
}

type ButtonProps = (
  | { color?: keyof typeof styles.colors; outline?: never; plain?: never }
  | { color?: never; outline: true; plain?: never }
  | { color?: never; outline?: never; plain: true }
) & { className?: string; children: ReactNode } & (
    | Omit<Headless.ButtonProps, 'as' | 'className'>
    | Omit<ComponentProps<typeof Link>, 'className'>
  ) & {
    ref?: Ref<HTMLAnchorElement>;
  };

/**
 * -----------------------------------------------------------------------------
 * This is the base building block component
 */
export function ButtonTw({
  children,
  className,
  color,
  outline,
  plain,
  ref,
  ...props
}: ButtonProps) {
  const classes = clsx(
    styles.base,
    outline
      ? styles.outline
      : plain
      ? styles.plain
      : clsx(styles.solid, styles.colors[color ?? 'dark/zinc']),
    className
  );

  return 'href' in props ? (
    <Link {...props} ref={ref as Ref<HTMLAnchorElement>} className={classes}>
      <TouchTarget>{children}</TouchTarget>
    </Link>
  ) : (
    <Headless.Button
      {...props}
      ref={ref}
      className={clsx('cursor-pointer', classes)}
    >
      <TouchTarget>{children}</TouchTarget>
    </Headless.Button>
  );
}
